import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Mail, Phone, MapPin, Scale, Lock, RefreshCcw, Info, Globe, Building2 } from "lucide-react";

/* ===== Legal information for Knya.ai =====
   Operated by: Nevika Innovations Private Limited
   CIN: U62090MH2026OPC472128 | GSTIN: 27AALCN7118G1Z9
   Last updated: 23 June 2026
   Designed to satisfy payment-gateway (Cashfree, Razorpay, Stripe) and standard SaaS compliance. */

export const LEGAL_BUSINESS        = "Nevika Innovations Private Limited";
export const LEGAL_BUSINESS_SHORT  = "Nevika Innovations Pvt. Ltd.";
export const LEGAL_TAGLINE         = "Infrastructure for the Future";
export const LEGAL_CIN             = "U62090MH2026OPC472128";
export const LEGAL_GST             = "27AALCN7118G1Z9";
export const LEGAL_WEBSITE         = "https://www.nevika.co";
export const LEGAL_EMAIL           = "info@nevika.co";
export const LEGAL_PHONE           = "+91 82919 98800";
export const LEGAL_PHONE_ALT       = "+91 96994 09888";
export const LEGAL_ADDRESS         = "1st Floor, Mapkhan Nagar, Opp. Municipal School, Marol, Marol Naka, Andheri (East), Mumbai – 400059, Maharashtra, India";
export const LEGAL_CONTACT_EMAIL   = "info@nevika.co";
export const LEGAL_SUPPORT_EMAIL   = "info@nevika.co";
export const LEGAL_LAST_UPDATED    = "23 June 2026";

const TABS = [
  { id: "about",    label: "About",        icon: Info },
  { id: "terms",    label: "Terms",        icon: Scale },
  { id: "privacy",  label: "Privacy",      icon: Lock },
  { id: "refund",   label: "Refunds",      icon: RefreshCcw },
  { id: "contact",  label: "Contact",      icon: Mail },
];

function Section({ children }) {
  return <section className="mt-6 space-y-3 text-[14px] leading-relaxed font-body text-[hsl(var(--foreground))]">{children}</section>;
}
function H({ children }) {
  return <h2 className="font-editorial text-[22px] leading-tight mt-5 text-[hsl(var(--plum))]">{children}</h2>;
}
function P({ children }) { return <p className="opacity-85">{children}</p>; }
function Strong({ children }) { return <span className="font-semibold">{children}</span>; }

function About() {
  return (
    <Section>
      <P>
        <Strong>Knya.ai</Strong> is a private women's wellness companion that helps users track their menstrual cycle, mood, sleep, fitness, skin, nutrition, pregnancy and safety contacts, with the help of an AI coach that delivers gentle, non-medical reflections.
      </P>
      <P>
        Knya.ai is a product of <Strong>{LEGAL_BUSINESS}</Strong> — {LEGAL_TAGLINE}. All product decisions, technical infrastructure, content and customer support are managed under {LEGAL_BUSINESS}.
      </P>
      <H>Our promise</H>
      <P>We do not sell user data. We do not run third-party advertising. We do not share personal health data with employers, insurers, or marketers.</P>
      <P className="text-[12px] opacity-60">Last updated: {LEGAL_LAST_UPDATED}</P>
    </Section>
  );
}

function Terms() {
  return (
    <Section>
      <H>1. Who we are</H>
      <P>
        These Terms govern your use of <Strong>Knya.ai</Strong> ("the Service"), a product of <Strong>{LEGAL_BUSINESS}</Strong> (CIN: {LEGAL_CIN}), a company incorporated in India and registered under the Companies Act 2013 (collectively, "we", "us", "our").
      </P>
      <H>2. Eligibility</H>
      <P>You must be at least 16 years of age (or the age of digital consent in your jurisdiction) to use Knya.ai. By creating an account, you confirm that you meet this requirement.</P>
      <H>3. Account & access</H>
      <P>Authentication is provided via your Google account. You are responsible for safeguarding your sign-in credentials. We may suspend access for abuse, fraud, illegal activity, or breach of these Terms.</P>
      <H>4. Acceptable use</H>
      <P>You agree not to (a) reverse engineer or scrape the Service, (b) attempt to access another user's data, (c) use the Service for unlawful purposes, or (d) submit content that is harmful, defamatory, or violates third-party rights.</P>
      <H>5. Not medical advice</H>
      <P>
        Knya.ai provides general wellness reflections only. The Service is <Strong>not a medical device</Strong> and does not provide medical diagnosis, treatment, or advice. Always consult a licensed healthcare provider for medical concerns. The AI coach is a supportive companion, not a clinician.
      </P>
      <H>6. Pricing</H>
      <P>Core features of Knya.ai are currently free of charge. If paid plans are introduced, pricing, billing cycle, and refund terms will be disclosed at the point of purchase, in addition to the Refund Policy on this page.</P>
      <H>7. Intellectual property</H>
      <P>The Knya.ai name, logo, design, copy, code and curated content are the intellectual property of <Strong>{LEGAL_BUSINESS}</Strong>. You retain ownership of the personal data you enter.</P>
      <H>8. Limitation of liability</H>
      <P>To the maximum extent permitted by law, Knya.ai and {LEGAL_BUSINESS} shall not be liable for indirect, incidental or consequential damages arising from your use of the Service. Our aggregate liability will not exceed the amount paid by you, if any, in the 12 months prior to the claim.</P>
      <H>9. Termination</H>
      <P>You may delete your account and request data erasure at any time by contacting {LEGAL_CONTACT_EMAIL}. We may suspend or terminate accounts that violate these Terms.</P>
      <H>10. Governing law</H>
      <P>These Terms are governed by the laws of India. Disputes shall be resolved in the courts of competent jurisdiction in Mumbai, Maharashtra, India.</P>
      <H>11. Contact</H>
      <P>For legal notices, write to <Strong>{LEGAL_BUSINESS}</Strong> at <Strong>{LEGAL_CONTACT_EMAIL}</Strong>.</P>
      <P className="text-[12px] opacity-60">Last updated: {LEGAL_LAST_UPDATED}</P>
    </Section>
  );
}

function Privacy() {
  return (
    <Section>
      <H>1. Data controller</H>
      <P>
        The data controller for Knya.ai is <Strong>{LEGAL_BUSINESS}</Strong> (CIN: {LEGAL_CIN}). Contact: {LEGAL_CONTACT_EMAIL}.
      </P>
      <H>2. What we collect</H>
      <P><Strong>Account data:</Strong> name, email, profile picture (from Google sign-in).</P>
      <P><Strong>Wellness data you enter:</Strong> cycle history, period dates, symptoms, moods, sleep, fitness, skin, nutrition, pregnancy (LMP, EDD), safety contacts, AI coach conversations, journal reflections.</P>
      <P><Strong>Technical data:</Strong> device type, IP, session timestamps used solely to keep the Service running and secure.</P>
      <H>3. How we use it</H>
      <P>To deliver predictions and insights to you, to power the AI coach, to keep your account secure, and to comply with legal obligations. We do <Strong>not</Strong> sell or rent your personal data, ever.</P>
      <H>4. AI processing</H>
      <P>AI replies are generated by third-party language models (OpenRouter / Anthropic / OpenAI / Google) under strict no-training agreements. Your prompts are sent securely and are not used to train upstream models.</P>
      <H>5. Storage & security</H>
      <P>Data is stored in encrypted MongoDB collections. Sessions are protected via httpOnly secure cookies. Access is restricted to {LEGAL_BUSINESS} and authorised contractors under NDA.</P>
      <H>6. Your rights</H>
      <P>You may request a full export, correction, or permanent deletion of your data at any time by writing to {LEGAL_CONTACT_EMAIL}. We will respond within 30 days.</P>
      <H>7. Cookies</H>
      <P>We use a single essential cookie ("session_token") to keep you signed in. No marketing or analytics cookies.</P>
      <H>8. Children</H>
      <P>Knya.ai is not directed at users under 16. We do not knowingly collect data from minors.</P>
      <H>9. International transfers</H>
      <P>If you access Knya.ai outside India, your data may be transferred to and processed on servers located outside your country, with safeguards equivalent to those required by your local law.</P>
      <H>10. Changes</H>
      <P>If we update this Policy, we will display the new effective date here and notify active users by email if changes are material.</P>
      <P className="text-[12px] opacity-60">Last updated: {LEGAL_LAST_UPDATED}</P>
    </Section>
  );
}

function Refund() {
  return (
    <Section>
      <H>Refund & Cancellation Policy</H>
      <P>
        Knya.ai is a product of <Strong>{LEGAL_BUSINESS}</Strong> (CIN: {LEGAL_CIN}). This Refund & Cancellation Policy applies to any paid purchase made through Knya.ai or via our authorised payment gateway partners (including Cashfree).
      </P>
      <H>1. Free tier</H>
      <P>The core Knya.ai experience is currently free of charge. No payment is collected for using the free tier.</P>
      <H>2. Paid plans (when offered)</H>
      <P>If you subscribe to a paid plan, you may request a full refund within <Strong>7 days</Strong> of the initial purchase, provided the plan has not been substantially consumed. Refunds for subsequent renewal periods are not provided unless required by applicable law.</P>
      <H>3. How to request</H>
      <P>Email {LEGAL_SUPPORT_EMAIL} from the same address used for the purchase. Include your order ID, the date of the transaction, and a short reason. Refund requests are reviewed within <Strong>3 business days</Strong> and approved refunds are issued to the original payment instrument within <Strong>7–10 business days</Strong>.</P>
      <H>4. Cancellation</H>
      <P>You can cancel a recurring subscription at any time from your account settings. Cancellation prevents future renewals; it does not retroactively refund completed billing cycles.</P>
      <H>5. Chargebacks</H>
      <P>If you believe a charge is incorrect, please contact us before initiating a chargeback with your bank or card network. We respond to legitimate disputes promptly.</P>
      <H>6. Contact</H>
      <P>{LEGAL_BUSINESS} · {LEGAL_SUPPORT_EMAIL} · {LEGAL_PHONE}</P>
      <P className="text-[12px] opacity-60">Last updated: {LEGAL_LAST_UPDATED}</P>
    </Section>
  );
}

function Contact() {
  return (
    <Section>
      <H>Contact us</H>
      <div className="surface-ivory plush-shadow-sm rounded-2xl p-4 mt-2 space-y-3 border border-[hsl(var(--border))]" data-testid="legal-contact-card">

        {/* Company identity */}
        <div className="flex items-start gap-3">
          <Building2 size={16} className="text-[hsl(var(--plum))] mt-1 shrink-0" />
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold opacity-60">Company</p>
            <p className="font-semibold mt-0.5" data-testid="legal-business-name">{LEGAL_BUSINESS}</p>
            <p className="text-[12px] opacity-70 mt-0.5 italic">{LEGAL_TAGLINE}</p>
          </div>
        </div>
        <div className="editorial-rule" />

        {/* Corporate credentials */}
        <div className="flex items-start gap-3">
          <Scale size={16} className="text-[hsl(var(--plum))] mt-1 shrink-0" />
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold opacity-60">Corporate credentials</p>
            <p className="text-[12.5px] mt-0.5 font-medium">CIN: <span className="font-semibold" data-testid="legal-cin">{LEGAL_CIN}</span></p>
            <p className="text-[12.5px] font-medium">GSTIN: <span className="font-semibold" data-testid="legal-gst">{LEGAL_GST}</span></p>
          </div>
        </div>
        <div className="editorial-rule" />

        {/* Address */}
        <div className="flex items-start gap-3">
          <MapPin size={16} className="text-[hsl(var(--plum))] mt-1 shrink-0" />
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold opacity-60">Registered office</p>
            <p className="text-[12.5px] mt-0.5 leading-relaxed">{LEGAL_ADDRESS}</p>
          </div>
        </div>
        <div className="editorial-rule" />

        {/* Email */}
        <div className="flex items-start gap-3">
          <Mail size={16} className="text-[hsl(var(--plum))] mt-1 shrink-0" />
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold opacity-60">Email</p>
            <a href={`mailto:${LEGAL_EMAIL}`} className="font-semibold mt-0.5 text-[hsl(var(--plum))] underline-offset-2 hover:underline text-[13px]">{LEGAL_EMAIL}</a>
          </div>
        </div>

        {/* Phone */}
        <div className="flex items-start gap-3">
          <Phone size={16} className="text-[hsl(var(--plum))] mt-1 shrink-0" />
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold opacity-60">Mobile</p>
            <a href={`tel:${LEGAL_PHONE}`} className="font-semibold mt-0.5 text-[hsl(var(--plum))] hover:underline text-[13px]">{LEGAL_PHONE}</a>
            <p className="text-[12px] opacity-70 mt-0.5">{LEGAL_PHONE_ALT} (alternate)</p>
          </div>
        </div>

        {/* Website */}
        <div className="flex items-start gap-3">
          <Globe size={16} className="text-[hsl(var(--plum))] mt-1 shrink-0" />
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold opacity-60">Website</p>
            <a href={LEGAL_WEBSITE} target="_blank" rel="noopener noreferrer" className="font-semibold mt-0.5 text-[hsl(var(--plum))] underline-offset-2 hover:underline text-[13px]">{LEGAL_WEBSITE}</a>
          </div>
        </div>
      </div>

      <H>Domain ownership</H>
      <P>The domain <Strong>knya.ai</Strong> and the trademark for "Knya.ai" are held by <Strong>{LEGAL_BUSINESS}</Strong>. Any communication regarding domain ownership, takedown notices, or intellectual property must be directed to {LEGAL_CONTACT_EMAIL}.</P>
      <P className="text-[12px] opacity-60">Last updated: {LEGAL_LAST_UPDATED}</P>
    </Section>
  );
}

export default function LegalPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // tab parameter from query or default to 'about'
  const params = new URLSearchParams(location.search);
  const initial = params.get("tab") || "about";
  const [tab, setTab] = React.useState(initial);

  React.useEffect(() => {
    setTab(params.get("tab") || "about");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const setTabAndUrl = (t) => {
    setTab(t);
    navigate(`/legal?tab=${t}`, { replace: true });
  };

  return (
    <div className="knya-page-pad-bottom" data-testid="legal-page">
      <header className="px-5 pt-8 pb-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} aria-label="Back" className="sparkle-dot" data-testid="legal-back-button">
          <ArrowLeft size={16} />
        </button>
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[hsl(var(--muted-foreground))]">Legal & info</p>
          <h1 className="font-editorial text-[28px] leading-tight">Knya.ai</h1>
        </div>
      </header>

      <div className="px-5">
        <div className="flex gap-1.5 overflow-x-auto scrollbar-thin pb-2" data-testid="legal-tabs">
          {TABS.map(({ id, label, icon: Icon }) => {
            const active = tab === id;
            return (
              <button
                key={id}
                onClick={() => setTabAndUrl(id)}
                data-testid={`legal-tab-${id}`}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12px] font-semibold border transition ${
                  active
                    ? "bg-[hsl(var(--plum))] text-[hsl(var(--ivory))] border-[hsl(var(--plum))]"
                    : "bg-[hsl(var(--ivory))] text-[hsl(var(--foreground))] border-[hsl(var(--border))] hover:border-[hsl(var(--plum))]"
                }`}
              >
                <Icon size={13} /> {label}
              </button>
            );
          })}
        </div>

        <div className="surface-ivory plush-shadow-sm rounded-[26px] p-5 mt-2 border border-[hsl(var(--border))]" data-testid={`legal-content-${tab}`}>
          {tab === "about"   && <About />}
          {tab === "terms"   && <Terms />}
          {tab === "privacy" && <Privacy />}
          {tab === "refund"  && <Refund />}
          {tab === "contact" && <Contact />}
        </div>

        <div className="pt-5 pb-3 text-center text-[11px] text-[hsl(var(--muted-foreground))]">
          <p>
            © {new Date().getFullYear()} <span className="font-semibold">Knya.ai by {LEGAL_BUSINESS_SHORT}</span>
          </p>
          <p className="opacity-80 mt-0.5">CIN: {LEGAL_CIN}</p>
          <p className="mt-1 font-editorial-italic text-[13px]">A quiet space, just yours.</p>
        </div>
      </div>
    </div>
  );
}
